/**
 * @file
 * This file defines all possible actions for the IX3 system. To add a new action:
 *
 * 1. Add your action in createActionsAndTypes with:
 *    myNewAction: defineAction('IX3_MY_NEW_ACTION')<PayloadType>()
 *
 *    IMPORTANT: Don't forget the second "()" invocation or TypeScript won't infer the payload!
 *
 * 2. Define your payload type inline or as a separate type:
 *    - Use void for actions without payloads
 *    - Make fields optional with ? if they aren't always needed
 *    - Consider reusing existing types from ix3/core
 *    - Add JSDoc comments above each field for better IDE tooltips
 *
 * Example:
 * ```typescript
 * ix3TimelineActionCreated: defineAction('IX3_TIMELINE_ACTION_CREATED')<{
 *   // Unique identifier of the timeline to create the action for.
 *   timelineId: TimelineId;
 *   // New Unique identifier of the action to create.
 *   id: TimelineActionId;
 *   // Optional preset to use for the action.
 *   presetId?: ActionPresetId;
 * }>(),
 * ```
 * Note: This file uses a centralized action definition pattern that differs from other stores
 * in the codebase. Instead of exporting individual action creators directly, we export:
 * - ix3Creators: Object containing all action creators
 * - ix3ActionTypes: Object containing all action type constants
 * - IX3Action: Union type of all possible actions
 *
 * This approach provides better TypeScript inference and maintains all IX3 actions
 * in a single, strongly-typed location.
 */

import {
  defineAction,
  createActionsAndTypes,
  type ActionsUnion,
} from './actionUtils';

import {
  ActionPresetGroupId,
  ActionPresetId,
  ComponentSeed,
  ConditionalPlayback,
  ExtensionKey,
  Interaction,
  InteractionId,
  Timeline,
  TimelineAction,
  TimelineActionId,
  TimelineId,
  TimelinePosition,
  TimingConfig,
  Trigger,
  TargetDefinition,
  ActionPresetAction,
  PageScope,
  TimelineRoleDefinition,
  StandardPlaybackControl,
} from '@packages/systems/ix3/core';
import type {TriggerMetadata} from '@packages/systems/ix3/schema';
import type {ComponentInteractionPatch} from './types';

import type {AnimationConfig} from '@packages/domains/theming/packs-common';

/**
 * Trigger-specific config for a timeline-group add/remove transition,
 * resolved from the trigger schema's `timelineGroupConfig` on the Designer
 * side (the store reducer has no registry access). When `roles` is present
 * the transition is role-based (tags timelines via `triggerMetadata.role`
 * instead of assigning a `groupId`) and flips the trigger's
 * `pluginConfig.multiTimeline`; otherwise the generic `groupId` flow runs.
 */
export type TimelineGroupTransitionConfig = {
  /** Roles assigned to each group in order; index 0 is the primary group. */
  roles?: readonly string[];
  /**
   * Names for each group in order, used by the generic `groupId` flow instead
   * of the default 'Action group N'. Indices past the end fall back to it.
   */
  groupNames?: readonly string[];
  /**
   * Extension key of the trigger this config was resolved FROM — the role
   * owner. Trigger order is authored data, so the owner isn't always
   * `triggers[0]` (a schema-valid interaction can list a Click first and the
   * role-owning Hover second). The reducer needs the key because it has no
   * registry access: role ownership drives which triggers get
   * `pluginConfig.multiTimeline` flipped, which one carries the group Control,
   * and which `pluginConfig` supplies `primaryRoleByPluginConfig`. Omitted
   * (legacy/direct callers) falls back to `triggers[0]`'s key. The reducer
   * resolves the instance from live state as the first new-model trigger of
   * this key — the same one the Designer's owner search picks — so a concurrent
   * edit that reorders triggers can't redirect the transition.
   */
  ownerExtensionKey?: ExtensionKey;
  /** Control applied to the trigger/timelines when one group remains. */
  singleGroupControl?: StandardPlaybackControl;
  /** Control applied to the trigger/timelines when multiple groups exist. */
  multiGroupControl?: StandardPlaybackControl;
  /**
   * The trigger's own group cap (e.g. Hover's 2). Enforced defensively on the
   * generic (`groupId`) path of the IX3_TIMELINE_GROUP_ADDED reducer so a stale
   * double-dispatch can't push a trigger past its cap even though the Designer
   * hides the button at it. The role path caps at `roles.length` instead.
   */
  maxGroups?: number;
  /**
   * `pluginConfig` keys to drop from the group-owning trigger when the
   * interaction expands past one group, for settings that only mean something
   * in single-group mode. Declared by the plugin (`timelineGroupConfig`) so the
   * reducer stays plugin-agnostic. Hover clears `eventMode`: with two role
   * groups both mouse events have to fire to drive their respective timelines,
   * so a narrowed Type would silently leave one group dead. The Designer locks
   * the Type selector in that state. The cleared values are PARKED on the
   * trigger (see `MULTI_GROUP_PLUGIN_CONFIG_STASH` in the reducer) and restored
   * when the interaction collapses back to one group — re-deriving them from
   * the surviving role can't work ('enter' and 'both' both keep the mouseEnter
   * row), and dropping them would silently re-enable a mouse event the author
   * had turned off.
   */
  clearPluginConfigOnMultiGroup?: readonly string[];
  /**
   * Which declared role the interaction's EXISTING timeline keeps when the
   * interaction expands past one group, chosen from a `pluginConfig` value on
   * the group-owning trigger (`key` names the field, `roles` maps its value to
   * a role in `roles`). Declared by the plugin so the reducer stays
   * plugin-agnostic. Hover maps `eventMode` so a Hover already narrowed to
   * "Mouse leave" keeps its authored actions on the hover-out role rather than
   * having them promoted to the hover-in one. Unmapped values keep declaration
   * order.
   */
  primaryRoleByPluginConfig?: {
    key: string;
    roles: Readonly<Record<string, string>>;
  };
};

export const {creators: ix3Creators, actionTypes: ix3ActionTypes} =
  createActionsAndTypes({
    // ============================================================================
    // Action Presets State
    // ============================================================================

    ix3ActionPresetCreated: defineAction('IX3_ACTION_PRESET_CREATED')<{
      /** New unique identifier for the preset. */
      id: ActionPresetId;
      /** Display name of the preset. */
      name: string;
      /** Determines whether interaction is soft deleted */
      deleted: boolean;
      /** Optional description of the preset. */
      description?: string;
      /** The action data to save in the preset */
      action: ActionPresetAction;
      /**
       * Optional group to assign the preset to.
       * - If not set, preset is ungrouped.
       * - If set to 'new', a new group will be created with the given name.
       * - If set to 'existing', the preset will be assigned to the given group.
       */
      group?: {
        /** New name for the new group. */
        newName: string;
        /** Unique identifier of the new group if new, otherwise existing. */
        id: ActionPresetGroupId;
      };
    }>(),

    ix3ActionPresetDuplicated: defineAction('IX3_ACTION_PRESET_DUPLICATED')<{
      /** Unique identifier of the preset to duplicate. */
      id: ActionPresetId;
      /** Unique identifier of the new preset to create. */
      newId: ActionPresetId;
      /** New display name for the new preset. */
      newName: string;
    }>(),

    ix3ActionPresetEdited: defineAction('IX3_ACTION_PRESET_SETTINGS_EDITED')<{
      /** Unique identifier of the preset. */
      id: ActionPresetId;
      /** Optional new display name of the preset. */
      name?: string;
      /** Optional new description of the preset. */
      description?: string;
    }>(),

    ix3ActionPresetConfigEdited: defineAction(
      'IX3_ACTION_PRESET_ACTION_EDITED'
    )<{
      /** Unique identifier of the preset to update. */
      id: ActionPresetId;
      /** Optional new timeline configuration for the preset. */
      action: ActionPresetAction;
    }>(),

    ix3ActionPresetDeleted: defineAction('IX3_ACTION_PRESET_DELETED')<{
      /** Unique identifier of the preset to delete. */
      id: ActionPresetId;
    }>(),

    // ============================================================================
    // Interactions State
    // ============================================================================

    ix3InteractionCreated: defineAction('IX3_INTERACTION_CREATED')<{
      /** Unique identifier for the new interaction. */
      id: InteractionId;
      /** Display name of the interaction. */
      name: string;
      /** Array of triggers that initiate the interaction. */
      triggers: Trigger[];
      /** Optional scope for the interaction. */
      scope?: PageScope;
      /** Optional metadata for analytics tracking */
      metadata?: {source?: 'left panel' | 'right panel'};
      /**
       * Optional configuration for multi-timeline triggers.
       * When provided, multiple timelines will be created for this interaction,
       * one for each role defined in the array.
       */
      multiTimelineConfig?: {
        /** The extension key of the trigger creating multiple timelines */
        triggerExtensionKey: ExtensionKey;
        /** Array of timeline role definitions */
        roles: TimelineRoleDefinition[];
      };
      /**
       * Names of the `groupId` action groups to create with the interaction,
       * for triggers whose group set is fixed rather than user-managed
       * (`timelineGroupConfig.fixedGroups`, added to `DesignBuilder` in #122805 — Scroll direction's scroll-down /
       * scroll-up pair). One timeline per name, in order. Ignored when
       * {@link multiTimelineConfig} is present, which owns its own role-based
       * timeline set.
       */
      timelineGroupNames?: readonly string[];
    }>(),

    /**
     * Apply one component interaction's reconcile patch — the edits that bring a
     * Popover's (later a Modal's, a Slider's) open/close animation back in line
     * with the component's current settings.
     *
     * Raised when the component is first assigned to an interaction seeded
     * without one, and whenever its settings change afterwards. One action
     * rather than a handful of granular ones so the whole adjustment is a single
     * step and none of the per-action coercion re-runs on data the seeder
     * already shaped deliberately.
     */
    ix3ComponentInteractionReconciled: defineAction(
      'IX3_COMPONENT_INTERACTION_RECONCILED'
    )<{
      /** Unique identifier of the interaction to reconcile. */
      interactionId: InteractionId;
      /** The derived edits; see `ComponentInteractionPatch`. */
      patch: ComponentInteractionPatch;
    }>(),

    /**
     * Records a component interaction's settings baseline without applying a
     * reconcile patch — the bookkeeping half of `IX3ComponentInteractionReconciler`
     * (a first sighting, or a set that no longer matches the recorded one).
     *
     * A separate action from `ix3ComponentInteractionReconciled`, even though
     * both set `componentSeed`: the reconcile applies a patch, and holds its
     * baseline back when an edit in that patch was skipped, whereas this records
     * the baseline unconditionally for the case with nothing visible to apply.
     * Both stay off the user's undo stack (see `skipUndoRedoUpdateActions` in
     * `MultiplayerStore.ts`): merely opening the Designer can reach this, and
     * undoing it removes bookkeeping instead of reverting the user's last edit.
     */
    ix3ComponentBaselineRecorded: defineAction(
      'IX3_COMPONENT_BASELINE_RECORDED'
    )<{
      /** Unique identifier of the interaction to record a baseline for. */
      interactionId: InteractionId;
      /** The baseline to store; see `componentSeedSchema`. */
      componentSeed: ComponentSeed;
    }>(),

    ix3InteractionRenamed: defineAction('IX3_INTERACTION_RENAMED')<{
      /** Unique identifier of the interaction to rename. */
      id: InteractionId;
      /** New name for the interaction. */
      name: string;
    }>(),

    /**
     * Create or replace an interaction from a complete, pre-validated payload,
     * unlike `ix3InteractionCreated` which starts empty. Defined here so undo,
     * multiplayer, and canvas resize treat it like any other interaction change.
     */
    ix3InteractionUpserted: defineAction('IX3_INTERACTION_UPSERTED')<{
      /** The fully-built interaction (client tuple shape). */
      interaction: Interaction;
      /** The interaction's complete timeline set (client tuple shape). */
      timelines: Timeline[];
      /**
       * Set only when a Designer Extension write raised this upsert, so the
       * host can resolve that request against its own commit rather than an
       * identical one from the panel, undo, multiplayer, or a second
       * extension request. The store ignores it.
       */
      extensionEffectId?: string;
      /**
       * Optional metadata for analytics tracking, matching
       * `ix3InteractionCreated`. Native seeded interactions — a Popover's
       * open/close from the settings panel or an add-interaction menu — come
       * through this action too, so without a source they would all report as
       * Designer Extension writes.
       */
      metadata?: {source?: 'left panel' | 'right panel'};
    }>(),

    ix3InteractionDeleted: defineAction('IX3_INTERACTION_DELETED')<{
      /** Unique identifier of the interaction to delete. */
      id: InteractionId;
      /** Optional metadata for analytics tracking */
      metadata?: {source?: 'left panel' | 'right panel'};
      /** See `ix3InteractionUpserted.extensionEffectId`. */
      extensionEffectId?: string;
    }>(),

    ix3InteractionDuplicated: defineAction('IX3_INTERACTION_DUPLICATED')<{
      /** Unique identifier of the interaction to duplicate. */
      id: InteractionId;
      /** Unique identifier of the new interaction to create. */
      newId: InteractionId;
      /** New display name for the new interaction. */
      newName: string;
      /** Optional metadata for analytics tracking */
      metadata?: {source?: 'left panel' | 'right panel'};
    }>(),

    ix3InteractionPageScopeUpdated: defineAction(
      'IX3_INTERACTION_PAGE_SCOPE_UPDATED'
    )<{
      /** Unique identifier of the interaction to update. */
      interactionId: InteractionId;
      /** Page scope configuration to set. */
      pageScope: PageScope;
    }>(),

    ix3InteractionVariantScopeUpdated: defineAction(
      'IX3_INTERACTION_VARIANT_SCOPE_UPDATED'
    )<{
      /** Unique identifier of the interaction to update. */
      interactionId: InteractionId;
      /** Array of variant option IDs, or undefined for "All variants". */
      variants: string[] | undefined;
    }>(),

    ix3InteractionTimelineDefaultsUpdated: defineAction(
      'IX3_INTERACTION_TIMELINE_DEFAULTS_UPDATED'
    )<{
      /** Unique identifier of the interaction to update. */
      interactionId: InteractionId;
      /** Timeline defaults configuration to set. */
      timelineDefaults: TimingConfig | null;
    }>(),

    ix3InteractionConditionalPlaybackUpdated: defineAction(
      'IX3_INTERACTION_CONDITIONAL_PLAYBACK_UPDATED'
    )<{
      /** Unique identifier of the interaction to update */
      interactionId: InteractionId;
      /** Conditional playback configuration to set */
      conditionalPlayback: ConditionalPlayback | null;
    }>(),

    ix3EditInteractionSelected: defineAction('IX3_EDIT_INTERACTION_SELECTED')<{
      /** Unique identifier of the interaction to select. */
      id: InteractionId;
      /** Optional metadata for analytics tracking */
      metadata?: {source?: 'left panel' | 'right panel'};
    }>(),

    ix3TimelineActionCreated: defineAction('IX3_TIMELINE_ACTION_CREATED')<{
      /** Unique identifier of the timeline to create the action for. */
      timelineId: TimelineId;
      /** New Unique identifier of the action to create. */
      id: TimelineActionId;
      /** Optional preset to use for the action. */
      presetId?: ActionPresetId;
      /** Plugin preset data (for presets not in BUILT_IN_PRESETS or store) */
      pluginPresetData?: {
        action: ActionPresetAction;
        type: 'built-in' | 'plugin';
      };
      /** Optional position to set for the action. */
      position?: TimelinePosition;
      /** Optional index to set for the action. */
      index?: number;
      /** Target array for the action (required, created by UI layer). */
      target: TargetDefinition[];
      /** Optional metadata for analytics tracking */
      metadata?: {
        source?: string;
        trigger?: 'click' | 'double click' | 'hotkey' | 'context menu';
      };
      /**
       * The editor's content-aware percent canvas length in seconds (from
       * `useEffectiveCanvasDuration`), passed so the reducer clamps a new
       * positional action against the same canvas the UI shows. The reducer's
       * own fallback only sees base action durations, so it underestimates the
       * canvas when existing actions have stagger/split-text extent; this lets
       * the clamp honor that extent. Omitted off a percent canvas.
       */
      effectiveCanvasDuration?: number;
    }>(),

    ix3TimelineActionEdited: defineAction('IX3_TIMELINE_ACTION_EDITED')<{
      /** Unique identifier of the timeline to edit the action for. */
      timelineId: TimelineId;
      /** The updated timeline action. */
      timelineAction: TimelineAction;
      /**
       * Percent canvas only: pins the gesture end, same as
       * `ix3TimelineActionsBulkEdited`'s `canvasDurationAnchor`. Carried here so
       * a shrinking single-action edit stays one `IX3_TIMELINE_ACTION_EDITED`
       * (which analytics reads) rather than switching to the bulk action.
       */
      canvasDurationAnchor?: number;
    }>(),

    ix3TimelineActionHiddenToggled: defineAction(
      'IX3_TIMELINE_ACTION_HIDDEN_TOGGLED'
    )<{
      /** Unique identifier of the timeline containing the action. */
      timelineId: TimelineId;
      /** Unique identifier of the action to toggle hidden state. */
      actionId: TimelineActionId;
    }>(),

    ix3TimelineActionPluginConfigPatched: defineAction(
      'IX3_TIMELINE_ACTION_PLUGIN_CONFIG_PATCHED'
    )<{
      /** Unique identifier of the timeline containing the action to patch. */
      timelineId: TimelineId;
      /** Unique identifier of the action to patch. */
      actionId: TimelineActionId;
      /** Plugin-owned config fields to shallow-merge onto the action. */
      pluginConfig: NonNullable<TimelineAction['pluginConfig']>;
    }>(),

    ix3TimelineActionsBulkEdited: defineAction(
      'IX3_TIMELINE_ACTIONS_BULK_EDITED'
    )<{
      /** Unique identifier of the timeline to edit the actions for. */
      timelineId: TimelineId;
      /** Array of updated timeline actions. */
      timelineActions: TimelineAction[];
      /**
       * Percent canvas only: the effective canvas length (seconds) at the time
       * of this edit, pinned into the timeline's `canvasDuration` marker so a
       * shrinking edit can't drag the Scroll/Move end marker in with it. Applied
       * grow-only and capped at the schema max; omit when the edit can't shrink
       * the canvas.
       */
      canvasDurationAnchor?: number;
    }>(),

    ix3TimelineActionSelected: defineAction('IX3_TIMELINE_ACTION_SELECTED')<{
      /** Unique identifier of the action to select. */
      id: TimelineActionId;
    }>(),

    ix3TimelineActionsMultiSelected: defineAction(
      'IX3_TIMELINE_ACTIONS_MULTI_SELECTED'
    )<{
      /** Array of unique identifiers of the actions to select. */
      ids: TimelineActionId[];
      /**
       * Whether to replace the current selection with the new selection.
       * This is used to support being able to add/remove actions
       * while dragging the selection box (add) and clicking selected actions (remove).
       *
       * When replace is true (default)... it will replace the values in the collection.
       * @example
       * Previous: [1, 2, 3]
       * Next: [4, 5, 6]
       * Output: [4, 5, 6]
       *
       * @example
       * Previous: [1, 2, 3]
       * Next: [3]
       * Output: [3]
       *
       * When replace is false... it will add the new values to the collection.
       * @example
       * Previous: [1, 2, 3]
       * Next: [4, 5, 6]
       * Output: [1, 2, 3, 4]
       *
       * replace: false is used for coordinating adding/removing items when:
       * 1. The selection box is dragged over items.
       * 2. The user is (shift) clicking items to add (or remove) them from the selection.
       */
      replace?: boolean;
    }>(),

    ix3TimelineActionSelectionCleared: defineAction(
      'IX3_TIMELINE_ACTION_SELECTION_CLEARED'
    )<void>(),

    ix3TimelineActionRenamed: defineAction('IX3_TIMELINE_ACTION_RENAMED')<{
      /** Unique identifier of the timeline to rename the action for. */
      timelineId: TimelineId;
      /** Unique identifier of the action to rename. */
      id: TimelineActionId;
      /** New display name for the action. */
      name: string;
    }>(),

    ix3TimelineActionDuplicated: defineAction(
      'IX3_TIMELINE_ACTION_DUPLICATED'
    )<{
      /** Unique identifier of the timeline to duplicate the action for. */
      timelineId: TimelineId;
      /** Unique identifier of the action to duplicate. */
      id: TimelineActionId;
      /** Unique identifier of the new action to create. */
      newId: TimelineActionId;
      /** New display name for the new action. */
      newName?: string;
      /** Optional metadata for analytics tracking */
      metadata?: {
        source?: string;
        trigger?: 'click' | 'double click' | 'hotkey' | 'context menu';
      };
    }>(),

    ix3TimelineActionsBulkDuplicated: defineAction(
      'IX3_TIMELINE_ACTIONS_BULK_DUPLICATED'
    )<{
      /** Unique identifier of the timeline to duplicate the actions for. */
      timelineId: TimelineId;
      /** Array of action IDs to duplicate with their corresponding new IDs. */
      duplications: Array<{
        /** Original action ID to duplicate. */
        originalId: TimelineActionId;
        /** New unique identifier for the duplicated action. */
        newId: TimelineActionId;
        /** Optional new display name for the duplicated action. */
        newName?: string;
      }>;
    }>(),

    ix3TimelineActionDeleted: defineAction('IX3_TIMELINE_ACTION_DELETED')<{
      /** Unique identifier of the timeline to delete the action from. */
      timelineId: TimelineId;
      /** Unique identifier of the action to delete. */
      id: TimelineActionId;
      /** Optional metadata for analytics tracking */
      metadata?: {
        source?: string;
        trigger?: 'click' | 'hotkey' | 'context menu';
      };
    }>(),

    ix3TimelineActionsBulkDeleted: defineAction(
      'IX3_TIMELINE_ACTIONS_BULK_DELETED'
    )<{
      /** Unique identifier of the timeline to delete the actions from. */
      timelineId: TimelineId;
      /** Array of unique identifiers of the actions to delete. */
      ids: TimelineActionId[];
    }>(),

    ix3TimelineActionSortedByStartTime: defineAction(
      'IX3_TIMELINE_ACTION_SORTED_BY_START_TIME'
    )<{
      /** Unique identifier of the timeline to sort the actions for. */
      timelineId: TimelineId;
    }>(),

    ix3TimelineActionReordered: defineAction('IX3_TIMELINE_ACTION_REORDERED')<{
      /** Unique identifier of the timeline to reorder the actions for. */
      timelineId: TimelineId;
      /** Array of action IDs to reorder. */
      ids: TimelineActionId[];
      /** New index for the actions. */
      newIndex: number;
    }>(),

    ix3TimelineEdited: defineAction('IX3_TIMELINE_EDITED')<{
      /** Unique identifier of the timeline to edit. */
      id: TimelineId;
      /** Optional new preview speed multiplier for the timeline. */
      previewSpeed?: number;
      /** Optional new play in reverse setting for the timeline. */
      playInReverse?: boolean;
      /** Optional new immediate setting for the timeline. */
      immediate?: boolean;
    }>(),

    ix3TimelineSettingsUpdated: defineAction('IX3_TIMELINE_SETTINGS_UPDATED')<{
      /** Unique identifier of the timeline to update. */
      timelineId: TimelineId;
      /** Timeline settings configuration to set. */
      settings: TimingConfig | null;
    }>(),

    // ============================================================================
    // Trigger State
    // ============================================================================

    ix3TriggerCreated: defineAction('IX3_TRIGGER_CREATED')<{
      /** Trigger definition to create. */
      trigger: Trigger;
      /** Unique identifier of the interaction to create the trigger for. */
      interactionId: InteractionId;
      /**
       * The component reconcile this edit calls for, applied in the same
       * reducer pass so the edit and its derived patch are one undo step. See
       * `applyComponentInteractionPatch` in `store.ts`.
       */
      componentPatch?: ComponentInteractionPatch;
    }>(),

    ix3TriggerUpdated: defineAction('IX3_TRIGGER_UPDATED')<{
      /** Index of the trigger to update. */
      index: number;
      /** Unique identifier of the interaction to update the trigger for. */
      interactionId: InteractionId;
      /** Updated trigger definition. */
      trigger: Trigger;
      /**
       * The component reconcile this edit calls for, applied in the same
       * reducer pass so the edit and its derived patch are one undo step. See
       * `applyComponentInteractionPatch` in `store.ts`.
       */
      componentPatch?: ComponentInteractionPatch;
    }>(),

    ix3TriggerDeleted: defineAction('IX3_TRIGGER_DELETED')<{
      /** Index of the trigger to delete. */
      index: number;
      /** Unique identifier of the interaction to delete the trigger from. */
      interactionId: InteractionId;
      /**
       * The component reconcile this edit calls for, applied in the same
       * reducer pass so the edit and its derived patch are one undo step. See
       * `applyComponentInteractionPatch` in `store.ts`.
       */
      componentPatch?: ComponentInteractionPatch;
    }>(),

    /** Assign a trigger to control a specific timeline group */
    ix3TriggerTimelineAssigned: defineAction('IX3_TRIGGER_TIMELINE_ASSIGNED')<{
      /** Unique identifier of the interaction. */
      interactionId: InteractionId;
      /** Index of the trigger to assign. */
      triggerIndex: number;
      /**
       * Id of the timeline group this trigger should control. Pass `null` to
       * unbind the trigger from its group (plays nothing), mirroring how group
       * deletion clears `assignedGroupId`. Pass `undefined` to return the
       * trigger to the play-all default (drives every group), so an assigned
       * trigger isn't stuck routing to a single group.
       */
      groupId: string | null | undefined;
    }>(),

    /**
     * Assign a trigger to control one timeline ROLE — the other routing axis.
     * Role-based interactions (a Popover's Open/Close) have no user-managed
     * groups, so `assignedGroupId` has nothing to point at.
     */
    ix3TriggerTimelineRoleAssigned: defineAction(
      'IX3_TRIGGER_TIMELINE_ROLE_ASSIGNED'
    )<{
      /** Unique identifier of the interaction. */
      interactionId: InteractionId;
      /** Index of the trigger to assign. */
      triggerIndex: number;
      /**
       * `triggerMetadata.role` of the timeline this trigger should drive. Pass
       * `null` to return the trigger to the unassigned state (drives nothing),
       * matching how a newly added trigger arrives.
       */
      role: string | null;
    }>(),

    /**
     * Narrow which component instances a trigger on a component interaction
     * drives — the popovers today, a Modal's or Slider's instances next.
     *
     * A component trigger (`wf:popover`) is one instance by construction, so
     * this is for the other triggers an author adds alongside it — a Click, Page
     * load, Exit intent or Duration assigned to Open or Close.
     */
    ix3TriggerInstancesAssigned: defineAction(
      'IX3_TRIGGER_INSTANCES_ASSIGNED'
    )<{
      /** Unique identifier of the interaction. */
      interactionId: InteractionId;
      /** Index of the trigger to assign. */
      triggerIndex: number;
      /**
       * `[componentId, elementId]` paths of the instances this trigger drives.
       * Pass `undefined` to return the trigger to driving every instance the
       * interaction names, which is how a newly added trigger arrives and what
       * keeps an instance added later included. An empty array narrows it to
       * none.
       */
      instances: Array<[string, string]> | undefined;
    }>(),

    /**
     * Turn "only fire whichever comes first" on or off for every page-level
     * one-shot trigger (Exit intent, Duration) on the interaction at once. The
     * option is one checkbox shown on each of them, so it is written to all
     * of them in one step.
     */
    ix3OneShotFirstWinsChanged: defineAction(
      'IX3_ONE_SHOT_FIRST_WINS_CHANGED'
    )<{
      /** Unique identifier of the interaction. */
      interactionId: InteractionId;
      firstWins: boolean;
    }>(),

    // ============================================================================
    // Timeline Groups
    // ============================================================================

    /** Add a new timeline group to the interaction */
    ix3TimelineGroupAdded: defineAction('IX3_TIMELINE_GROUP_ADDED')<{
      /** Unique identifier of the interaction. */
      interactionId: InteractionId;
      /**
       * Optional trigger-specific transition config (e.g. Hover). When
       * `groupConfig.roles` is present, the new group is role-based rather
       * than a generic `groupId` group. Omit for the generic flow.
       */
      groupConfig?: TimelineGroupTransitionConfig;
    }>(),

    /** Remove a timeline group from the interaction */
    ix3TimelineGroupRemoved: defineAction('IX3_TIMELINE_GROUP_REMOVED')<{
      /** Unique identifier of the interaction. */
      interactionId: InteractionId;
      /** Unique identifier of the timeline to remove. */
      timelineId: TimelineId;
      /**
       * Optional trigger-specific transition config (e.g. Hover). Collapse
       * follows the remaining timeline: role-form Hover (enter/leave, no
       * `groupId`) takes role collapse even without `roles`; a remaining
       * `groupId` always takes generic collapse. Role collapse clears the
       * surviving role, reverts the Hover owner to `singleGroupControl`,
       * and drops parked Click `assignedGroupId` so that Click runs again.
       */
      groupConfig?: TimelineGroupTransitionConfig;
    }>(),

    /** Rename a timeline group */
    ix3TimelineGroupRenamed: defineAction('IX3_TIMELINE_GROUP_RENAMED')<{
      /** Unique identifier of the timeline to rename. */
      timelineId: TimelineId;
      /** New name for the timeline group. */
      name: string;
    }>(),

    /**
     * Set the playback control on the group-owning trigger and fan it out to
     * every grouped/role timeline's `settings.control`. Used by trigger types
     * whose Control lives at the trigger level but drive multiple timelines
     * (e.g. Hover), where the runtime reads per-timeline `settings.control`
     * for role/group timelines. Atomic so undo/multiplayer see one operation
     * rather than N timeline-settings updates. Dispatched only for triggers
     * that declare `timelineGroupConfig.allowedControls`, which none does
     * today.
     */
    ix3TriggerControlChanged: defineAction('IX3_TRIGGER_CONTROL_CHANGED')<{
      /** Unique identifier of the interaction. */
      interactionId: InteractionId;
      /**
       * Extension key of the trigger whose Control was edited. The reducer
       * writes the trigger-level `control` to THIS trigger only: Hover is
       * `exclusivity: 'normal'`, so a Click can sit beside it in the same
       * interaction with its own authored restart/toggle behavior, and that
       * field is live whenever the groups collapse back to one. Mirrors the
       * owner-only Control write in the IX3_TIMELINE_GROUP_ADDED reducer.
       */
      ownerExtensionKey: ExtensionKey;
      /**
       * Position of that trigger in `interaction.triggers` at dispatch time.
       * Advisory: the reducer validates the key at that position and falls back
       * to a key lookup when a concurrent edit reordered the list.
       */
      ownerTriggerIndex: number;
      /** New playback control to apply. */
      control: StandardPlaybackControl;
    }>(),

    /**
     * Patch a timeline's triggerMetadata with a partial update.
     *
     * Shallow-merges top-level fields (role/distance/axes). If `patch`
     * includes `pluginConfig`, that sub-object is shallow-merged too so
     * callers can update a single plugin field (e.g. `setMouseX`) without
     * clobbering the rest of the plugin config.
     */
    ix3TimelineTriggerMetadataPatched: defineAction(
      'IX3_TIMELINE_TRIGGER_METADATA_PATCHED'
    )<{
      /** Unique identifier of the timeline. */
      timelineId: TimelineId;
      /** Partial metadata to merge into the timeline's triggerMetadata. */
      patch: Partial<TriggerMetadata>;
    }>(),

    // ============================================================================
    // Target State
    // ============================================================================
    ix3TargetAdded: defineAction('IX3_TIMELINE_TARGET_ADDED')<{
      /** Unique identifier of the timeline to add the target to. */
      timelineId: TimelineId;
      /** Unique identifier of the action to add the target to. */
      actionId: TimelineActionId;
      /** Target definition to add. */
      target: TargetDefinition;
    }>(),

    ix3TargetDeleted: defineAction('IX3_TIMELINE_TARGET_DELETED')<{
      /** Unique identifier of the timeline to delete the target from. */
      timelineId: TimelineId;
      /** Unique identifier of the action to delete the target from. */
      actionId: TimelineActionId;
      /** Index of the target to delete. */
      targetIndex: number;
    }>(),

    ix3TargetTypeChanged: defineAction('IX3_TIMELINE_TARGET_TYPE_CHANGED')<{
      /** Unique identifier of the timeline to change the target type for. */
      timelineId: TimelineId;
      /** Unique identifier of the action to change the target type for. */
      actionId: TimelineActionId;
      /** Index of the target to change the type for. */
      targetIndex: number;
      /** Extension key for the target type from the registry. */
      extensionKey: ExtensionKey;
    }>(),

    // ============================================================================
    // UI State
    // ============================================================================

    ix3EditInteractionClosed: defineAction(
      'IX3_EDIT_INTERACTION_CLOSED'
    )<void>(),

    ix3EditTimelineActionClosed: defineAction(
      'IX3_EDIT_TIMELINE_ACTION_CLOSED'
    )<void>(),

    ix3UIShowAddAction: defineAction('IX3_UI_SHOW_ADD_ACTION')<void | {
      /** Optional payload to force showing or hiding the add action view. */
      showAddAction: boolean;
    }>(),

    ix3UIScrubChanged: defineAction('IX3_UI_SCRUB_CHANGED')<{
      isScrubbing: boolean;
    }>(),

    ix3UIResizeTimeline: defineAction('IX3_UI_TIMELINE_RESIZED')<{
      /** The new timeline height. */
      timelineHeight: number;
    }>(),

    ix3UIToggleCanvasTargetOutlines: defineAction(
      'IX3_UI_TOGGLE_CANVAS_TARGET_OUTLINES'
    )<{
      /** Optional payload to force showing or hiding the target outlines. */
      showOutlines: boolean;
    } | void>(),

    ix3UIToggleScrollPreviewMode: defineAction(
      'IX3_UI_TOGGLE_SCROLL_PREVIEW_MODE'
    )<void>(),

    ix3UIToggleTimeline: defineAction('IX3_UI_TIMELINE_TOGGLED')<{
      /** Optional payload to force showing or hiding a timeline. */
      showTimeline: boolean;
      /** Optional metadata for analytics tracking */
      metadata?: {trigger?: 'click' | 'shortcut'};
    } | void>(),

    ix3UITimelineIndexSelected: defineAction('IX3_UI_TIMELINE_INDEX_SELECTED')<{
      /**
       * Index of the timeline to select within the interaction's timelineIds array.
       * Used for multi-timeline triggers like Mouse Move (X/Y) and Navbar/Dropdown (open/close).
       */
      index: number;
    }>(),

    ix3TimelinePlaybackToggled: defineAction('IX3_TIMELINE_PLAYBACK_TOGGLED')<{
      /** Unique identifier of the timeline being played/paused. */
      timelineId: TimelineId;
      /** Whether playback is starting (true) or pausing (false). */
      isPlaying: boolean;
      /** How the playback was triggered */
      metadata?: {trigger?: 'click' | 'shortcut'};
    }>(),

    // ============================================================================
    // AI Site Builder
    // ============================================================================

    ix3AISBAnimationApplied: defineAction('IX3_AISB_ANIMATION_APPLIED')<{
      /** A pack comprises of a list of Animation Pack Configs */
      animationConfigs: AnimationConfig[];
      /** Optional: Remove previous AISB interactions (cleanup) */
      aisbInteractionsToRemove?: InteractionId[];
      /** Scope for the applied animations. If not provided, defaults to site-wide scope. */
      scope?: PageScope;
    }>(),
  });

export type IX3Action = ActionsUnion<typeof ix3Creators>;
